import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import LocalStorageService from "./local-storage-service";

// Headers that the Axios request interceptor (`interceptors.ts`)
// stamps on every outbound API call. When code paths bypass Axios —
// streaming downloads use raw `fetch` so the response body stays a
// ReadableStream, for example — the same headers still need to be
// added by hand or the backend rejects the request:
//   * Authorization     — Bearer token from localStorage
//   * domainName        — first path segment, blank on public routes
//   * currentProjectId  — required by [KCurrentProjectIdInHeader]
//                          endpoints (the table-editor export is one)
//   * X-ClientId        — user identity OR a generated client id;
//                          server-side audit needs this present
//
// This helper is the single source of truth so the fetch and Axios
// paths can't drift. Mirrors `interceptors.ts:57` line-for-line; any
// change there should be reflected here (and ideally a test below
// pins the contract).

declare let window: Window & {
  kuika?: { activeProjectId?: string };
  userInfo?: { id?: string };
};

const PUBLIC_DOMAIN_SEGMENTS: ReadonlySet<string> = new Set<string>(["signup", "login", "confirm-email"]);

const resolveDomainName = (): string => {
  if (typeof document === "undefined") {
    return "";
  }
  const path: string = document.location.pathname.substring(1);
  const first: string = path.split("/")[0] ?? "";
  if (first.length === 0) {
    return "";
  }
  const lower: string = first.toLowerCase();
  if (PUBLIC_DOMAIN_SEGMENTS.has(lower)) {
    return "";
  }
  return first;
};

const resolveClientId = (): string => {
  const userId: string | undefined = window?.userInfo?.id;
  if (typeof userId === "string" && userId.length > 0) {
    return userId;
  }
  return CommonHelperFunctions.getClientId();
};

// Returns the headers a raw fetch call needs to look indistinguishable
// from an Axios call to the same endpoint. Caller spreads into a
// `headers` object on `fetch()`; missing-token rows are omitted (rather
// than emitted as empty strings) so the backend's auth middleware
// treats the call as anonymous when it actually is.
export interface IAuthHeadersForFetch {
  Authorization?: string;
  domainName?: string;
  currentProjectId?: string;
  "X-ClientId"?: string;
}

export const authHeadersForFetch = (): IAuthHeadersForFetch => {
  const headers: IAuthHeadersForFetch = {};
  const token: string | null = LocalStorageService.getAccessToken();
  if (token !== null && token.length > 0) {
    headers.Authorization = `Bearer ${token}`;
    headers.domainName = resolveDomainName();
    const projectId: string | undefined = window?.kuika?.activeProjectId;
    if (typeof projectId === "string" && projectId.length > 0) {
      headers.currentProjectId = projectId;
    }
  }
  headers["X-ClientId"] = resolveClientId();
  return headers;
};
